import { useEffect, useState, useCallback } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { GitCompareArrows, RefreshCcw, Camera, Loader2, Plus, Minus, RefreshCw } from "lucide-react";
import MethodGlyph from "@/components/MethodGlyph";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function parseCid(cid) {
  const parts = (cid || "").split("|");
  const [manufacturer, model, version, year, engine, power, torque, ecuName, protocol, ecuType] = parts;
  return { manufacturer, model, version, year, engine, power, torque, ecuName, protocol, ecuType };
}

export default function Diff() {
  const [snapshots, setSnapshots] = useState([]);
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState("added");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/snapshots");
      setSnapshots(data.items || []);
      if (data.items && data.items.length >= 2) {
        if (!fromId) setFromId(data.items[1]._id);
        if (!toId) setToId(data.items[0]._id);
      } else if (data.items && data.items.length === 1) {
        if (!toId) setToId(data.items[0]._id);
      }
    } catch (err) {
      console.warn(err);
    }
  }, [fromId, toId]);

  useEffect(() => {
    load();
  }, [load]);

  const createSnapshot = async () => {
    setCreating(true);
    try {
      await api.post("/snapshots", null, { params: { label: `manual @ ${new Date().toISOString()}` } });
      toast.success("Snapshot erstellt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snapshot fehlgeschlagen");
    } finally {
      setCreating(false);
    }
  };

  const runDiff = async () => {
    if (!fromId || !toId) {
      toast.error("Bitte zwei Snapshots wählen");
      return;
    }
    if (fromId === toId) {
      toast.error("Bitte zwei verschiedene Snapshots wählen");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/snapshots/diff", {
        params: { from: fromId, to: toId, include_details: true, max_examples: 50 },
      });
      setDiff(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diff fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const deleteSnapshot = async (id) => {
    if (!window.confirm("Snapshot wirklich löschen?")) return;
    try {
      await api.delete(`/snapshots/${id}`);
      toast.success("Snapshot gelöscht");
      load();
    } catch (e) {
      toast.error("Konnte Snapshot nicht löschen");
    }
  };

  return (
    <div data-testid="diff-page" className="space-y-8">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D32222] mb-3">
            Snapshot Diff
          </div>
          <h1
            className="text-4xl sm:text-5xl leading-[1] tracking-tight"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            Was hat sich zwischen zwei Läufen geändert?
          </h1>
          <p className="mt-3 text-neutral-400 max-w-2xl text-sm">
            Vergleiche zwei Scrape-Snapshots und sieh sofort, welche
            Fahrzeuge/ECUs neu sind, welche verschwunden und welche geändert
            wurden (z. B. andere Read-Methoden).
          </p>
        </div>
        <Button
          data-testid="create-snapshot-btn"
          onClick={createSnapshot}
          disabled={creating}
          className="bg-[#D32222] hover:bg-[#E13636] text-white font-medium"
        >
          {creating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Camera className="w-4 h-4 mr-2" />
          )}
          Snapshot vom aktuellen Stand
        </Button>
      </div>

      {/* Snapshot list */}
      <div className="rounded-xl border border-neutral-800 bg-panel overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-neutral-500">
            Gespeicherte Snapshots
          </div>
          <Button
            data-testid="reload-snapshots"
            variant="ghost"
            size="sm"
            onClick={load}
            className="text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800"
          >
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Neu laden
          </Button>
        </div>
        {snapshots.length === 0 ? (
          <div className="p-8 text-center text-sm text-neutral-500">
            Noch keine Snapshots. Erstelle einen, dann führe einen Scrape aus,
            und erstelle einen zweiten — dann kannst du sie hier vergleichen.
          </div>
        ) : (
          <div className="divide-y divide-neutral-900">
            {snapshots.map((s) => (
              <div
                key={s._id}
                data-testid={`snapshot-row-${s._id}`}
                className="px-4 py-3 flex items-center gap-4 hover:bg-neutral-900/40 transition"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-neutral-100 truncate">
                    {s.label}
                  </div>
                  <div className="text-[11px] text-neutral-500 font-mono">
                    {s._id.slice(0, 8)}… · {formatDate(s.created_at)} ·{" "}
                    <span className="text-[#D32222]">
                      {s.item_count?.toLocaleString("de-DE")}
                    </span>{" "}
                    Einträge
                  </div>
                </div>
                <Button
                  data-testid={`delete-snapshot-${s._id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteSnapshot(s._id)}
                  className="text-neutral-500 hover:text-rose-400 hover:bg-neutral-800"
                >
                  Löschen
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Diff selector */}
      <div className="rounded-xl border border-neutral-800 bg-panel p-6 space-y-4">
        <div className="text-xs uppercase tracking-widest text-neutral-500">
          Zwei Snapshots vergleichen
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto] gap-3 items-end">
          <SnapshotSelect
            label="Von (älter)"
            testid="from-snapshot"
            value={fromId}
            onChange={setFromId}
            snapshots={snapshots}
          />
          <div className="text-neutral-600 text-xl hidden md:block pb-2">→</div>
          <SnapshotSelect
            label="Zu (neuer)"
            testid="to-snapshot"
            value={toId}
            onChange={setToId}
            snapshots={snapshots}
          />
          <Button
            data-testid="run-diff-btn"
            onClick={runDiff}
            disabled={loading || !fromId || !toId || fromId === toId}
            className="bg-[#D32222] hover:bg-[#E13636] text-white font-medium h-10"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <GitCompareArrows className="w-4 h-4 mr-2" />
            )}
            Vergleich starten
          </Button>
        </div>
      </div>

      {/* Diff Result */}
      {diff && (
        <div data-testid="diff-result" className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <DiffStat
              label="Neu"
              count={diff.added_count}
              accent="text-emerald-400"
              icon={<Plus className="w-4 h-4" />}
              testid="stat-added"
              active={tab === "added"}
              onClick={() => setTab("added")}
            />
            <DiffStat
              label="Entfernt"
              count={diff.removed_count}
              accent="text-rose-400"
              icon={<Minus className="w-4 h-4" />}
              testid="stat-removed"
              active={tab === "removed"}
              onClick={() => setTab("removed")}
            />
            <DiffStat
              label="Geändert"
              count={diff.changed_count}
              accent="text-[#E13636]"
              icon={<RefreshCw className="w-4 h-4" />}
              testid="stat-changed"
              active={tab === "changed"}
              onClick={() => setTab("changed")}
            />
          </div>

          <div className="rounded-xl border border-neutral-800 bg-panel overflow-hidden">
            <div className="px-4 py-3 border-b border-neutral-800 text-xs uppercase tracking-widest text-neutral-500">
              Beispiele ({tab})
            </div>
            <div className="divide-y divide-neutral-900">
              {tab === "added" && (diff.added_examples || []).length === 0 && (
                <Empty label="Keine neuen Einträge." />
              )}
              {tab === "added" &&
                (diff.added_examples || []).map((row, i) => (
                  <DiffRow key={i} row={row} kind="added" />
                ))}
              {tab === "removed" && (diff.removed_examples || []).length === 0 && (
                <Empty label="Keine entfernten Einträge." />
              )}
              {tab === "removed" &&
                (diff.removed_examples || []).map((r, i) => {
                  const p = parseCid(r.composite_id);
                  return (
                    <div
                      key={i}
                      className="px-4 py-3 grid grid-cols-1 md:grid-cols-12 gap-2 text-sm"
                      data-testid="diff-row-removed"
                    >
                      <div className="md:col-span-3 text-neutral-100">
                        {p.manufacturer}
                      </div>
                      <div className="md:col-span-3 text-neutral-300">
                        {p.model} {p.version}
                        <div className="text-[11px] text-neutral-500">
                          {p.year}
                        </div>
                      </div>
                      <div className="md:col-span-3 text-neutral-300">
                        {p.engine}
                      </div>
                      <div className="md:col-span-3 text-neutral-300 font-mono text-xs">
                        {p.ecuName} · {p.protocol} · {p.ecuType}
                      </div>
                    </div>
                  );
                })}
              {tab === "changed" && (diff.changed_examples || []).length === 0 && (
                <Empty label="Keine geänderten Einträge." />
              )}
              {tab === "changed" &&
                (diff.changed_examples || []).map((row, i) => (
                  <DiffRow key={i} row={row} kind="changed" />
                ))}
            </div>
            <div className="px-4 py-2 border-t border-neutral-900 text-[11px] text-neutral-500">
              Zeige die ersten 50 Beispiele. Gesamtzahlen oben.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SnapshotSelect({ label, testid, value, onChange, snapshots }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">
        {label}
      </div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testid}
          className="bg-neutral-900 border-neutral-800 text-neutral-100 h-10 focus:ring-[#D32222]"
        >
          <SelectValue placeholder="— Snapshot wählen —" />
        </SelectTrigger>
        <SelectContent className="bg-panel border-neutral-800 text-neutral-100 max-h-[300px]">
          {snapshots.map((s) => (
            <SelectItem
              key={s._id}
              value={s._id}
              data-testid={`${testid}-opt-${s._id}`}
              className="focus:bg-neutral-800"
            >
              <span className="truncate max-w-[240px] inline-block align-middle">
                {s.label}
              </span>{" "}
              <span className="text-[11px] text-neutral-500 font-mono">
                · {s.item_count?.toLocaleString("de-DE")}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DiffStat({ label, count, accent, icon, testid, active, onClick }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl border p-4 transition ${
        active
          ? "border-[#D32222]/60 bg-neutral-900"
          : "border-neutral-800 bg-panel hover:bg-neutral-900/50"
      }`}
    >
      <div className={`flex items-center gap-2 text-xs uppercase tracking-widest ${accent}`}>
        {icon} {label}
      </div>
      <div className="mt-2 text-3xl font-semibold text-neutral-100 tabular-nums">
        {count?.toLocaleString("de-DE") ?? 0}
      </div>
    </button>
  );
}

function DiffRow({ row, kind }) {
  const engine = row.engine || "";
  const model = [row.model, row.version].filter(Boolean).join(" ");
  const ecu = row.ecu_tcu || {};
  return (
    <div
      className="px-4 py-3 grid grid-cols-1 md:grid-cols-12 gap-2 text-sm"
      data-testid={`diff-row-${kind}`}
    >
      <div className="md:col-span-3 text-neutral-100 flex items-center gap-2">
        {row.manufacturer_logo && (
          <img
            src={row.manufacturer_logo}
            alt=""
            className="h-5 w-5 object-contain bg-neutral-900 border border-neutral-800 rounded p-0.5"
          />
        )}
        {row.manufacturer}
      </div>
      <div className="md:col-span-3 text-neutral-300">
        {model || "—"}
        <div className="text-[11px] text-neutral-500">{row.year}</div>
      </div>
      <div className="md:col-span-3 text-neutral-300">{engine || "—"}</div>
      <div className="md:col-span-3 text-neutral-300 text-xs font-mono">
        <div>{ecu.name || "—"}</div>
        <div className="text-neutral-500">
          {ecu.protocol?.label} · {ecu.type?.label}
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {(row.methods || []).map((m, i) => (
            <Badge
              key={i}
              variant="outline"
              title={m.label}
              className="border-neutral-700 text-neutral-300 text-[10px] font-mono uppercase"
            >
              <MethodGlyph label={m.label} className="w-9 h-9" />
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="p-8 text-center text-sm text-neutral-500">{label}</div>
  );
}
